import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import {
  parseStoredConsent,
  effectivePrefs,
  isUsDefault,
  type StoredConsent,
  type GeoState,
} from "@/lib/consentDecision";
import { resolveVisitorCountry, globalPrivacyControlOn } from "@/lib/geo";

export interface CookiePreferences {
  functional: boolean;
  analytics: boolean;
}

interface CookieConsentState extends CookiePreferences {
  /** True only when the visitor has a stored choice. */
  hasConsented: boolean;
  /** Stored record read from storage (first render is always "not loaded"). */
  consentLoaded: boolean;
  /** "pending" while the country lookup runs; only looked up with no stored choice. */
  geo: GeoState;
  gpc: boolean;
  /** Functional is on solely because of the US default (no stored choice). */
  usDefaultActive: boolean;
  acceptAll: () => void;
  acceptEssentialOnly: () => void;
  updatePreferences: (prefs: Partial<CookiePreferences>) => void;
}

const STORAGE_KEY = "cookie_consent";

const CookieConsentContext = createContext<CookieConsentState | null>(null);

function loadConsent(): StoredConsent | null {
  try {
    return parseStoredConsent(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function saveConsent(prefs: StoredConsent) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredConsent | null>(null);
  const [consentLoaded, setConsentLoaded] = useState(false);
  const [geo, setGeo] = useState<GeoState>("pending");
  const [gpc] = useState<boolean>(() => globalPrivacyControlOn());

  useEffect(() => {
    const s = loadConsent();
    setStored(s);
    setConsentLoaded(true);
    // The country only matters while there is no stored choice.
    if (s) { setGeo(null); return; }
    let cancelled = false;
    resolveVisitorCountry()
      .then((c) => { if (!cancelled) setGeo(c); })
      .catch(() => { if (!cancelled) setGeo(null); });
    return () => { cancelled = true; };
  }, []);

  const prefs = useMemo(
    () => (consentLoaded ? effectivePrefs(stored, geo, gpc) : { functional: false, analytics: false }),
    [consentLoaded, stored, geo, gpc],
  );

  const persist = useCallback((next: CookiePreferences) => {
    const rec: StoredConsent = { functional: !!next.functional, analytics: !!next.analytics, source: "user" };
    setStored(rec);
    saveConsent(rec);
  }, []);

  const acceptAll = useCallback(() => persist({ functional: true, analytics: true }), [persist]);
  const acceptEssentialOnly = useCallback(() => persist({ functional: false, analytics: false }), [persist]);
  const updatePreferences = useCallback(
    (partial: Partial<CookiePreferences>) => persist({ ...prefs, ...partial }),
    [persist, prefs],
  );

  const usDefaultActive = consentLoaded && !stored && isUsDefault(geo, gpc);

  return (
    <CookieConsentContext.Provider
      value={{
        ...prefs,
        hasConsented: !!stored,
        consentLoaded,
        geo,
        gpc,
        usDefaultActive,
        acceptAll,
        acceptEssentialOnly,
        updatePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentState {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}

/**
 * Non-throwing variant for trees that may sit outside the provider
 * (e.g. the Exchange app). Returns null when there is no provider.
 */
export function useCookieConsentOptional(): CookieConsentState | null {
  return useContext(CookieConsentContext);
}
